import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineHealthAndSafety,
  MdOutlineMap,
  MdOutlineSubtitles,
  MdPeopleOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { DATE_FORMAT, GENDER, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Adherent = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const NATIONALITIES = p("DETAIL.NATIONALITIES", { returnObjects: true });

  const genderPath = "project.contact.user.gender";
  const genderField = bindField(genderPath, {
    label: SUBSCRIPTION.FORM.GENDER.LABEL,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const lastnamePath = "project.contact.user.lastname";
  const lastnameField = bindField(lastnamePath, {
    label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
    required: isFieldRequired(lastnamePath),
    disabled: true,
  });

  const firstnamePath = "project.contact.user.firstname";
  const firstnameField = bindField(firstnamePath, {
    label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
    required: isFieldRequired(firstnamePath),
    disabled: true,
  });

  const birthDatePath = "project.contact.user.birthDate";
  const birthDateField = bindField(birthDatePath, {
    label: SUBSCRIPTION.FORM.BIRTH_DATE.LABEL,
    required: isFieldRequired(birthDatePath),
    disabled: true,
  });

  const socialSecurityNumberPath = "project.contact.socialSecurityNumber";
  const socialSecurityNumberField = bindField(socialSecurityNumberPath, {
    label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
    required: isFieldRequired(socialSecurityNumberPath),
    extractValue: ({ target: { value } }) =>
      value.replace(/\D/g, "").slice(0, 15),
    onBlur: () => setPercent(initialValues),
  });

  const nationalityPath = "project.contact.nationality";
  const nationalityField = bindField(nationalityPath, {
    label: SUBSCRIPTION.FORM.NATIONALITY?.LABEL || "Nationalité",
    required: true,
    onChange: () => {
      setPercent(initialValues);
    },
  });

  const healthInsuranceFundPath = "project.contact.fields.healthInsuranceFund";
  const healthInsuranceFundField = bindField(healthInsuranceFundPath, {
    label:
      SUBSCRIPTION.FORM.HEALTH_INSURANCE_FUND?.LABEL ||
      "Caisse d'assurance maladie",
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.TITLE}
        subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ADHERENT.SUBTITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <FormField
              path={genderField.path}
              label={genderField.label}
              required={genderField.required}
              disabled={genderField.disabled}
            >
              {(field) => (
                <Input
                  type="radio"
                  variant="group"
                  icon={<MdPeopleOutline icon="man" size={16} />}
                  values={[
                    {
                      value: GENDER.MALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                    },
                    {
                      value: GENDER.FEMALE,
                      label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                    },
                  ]}
                  label={genderField.label}
                  value={get(initialValues, genderPath)}
                  {...field}
                  onChange={genderField.onChange}
                  onBlur={genderField.onBlur}
                  optional
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={lastnameField.path}
              label={lastnameField.label}
              required={lastnameField.required}
              disabled={lastnameField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                  label={lastnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  defaultValue={lastnameField.defaultValue}
                  {...field}
                  onChange={lastnameField.onChange}
                  onBlur={lastnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={firstnameField.path}
              label={firstnameField.label}
              required={firstnameField.required}
              disabled={firstnameField.disabled}
            >
              {(field) => (
                <Input
                  label={firstnameField.label}
                  icon={<MdOutlineBadge size={16} />}
                  placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                  defaultValue={firstnameField.defaultValue}
                  {...field}
                  onChange={firstnameField.onChange}
                  onBlur={firstnameField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={birthDateField.path}
              label={birthDateField.label}
              required={birthDateField.required}
              disabled={birthDateField.disabled}
            >
              {(field) => (
                <Input
                  placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                  type="date"
                  variant="simple"
                  label={birthDateField.label}
                  defaultValue={dayjs(get(initialValues, birthDatePath))}
                  icon={<MdOutlineCake size={16} />}
                  format={DATE_FORMAT}
                  {...field}
                  onChange={birthDateField.onChange}
                  onBlur={birthDateField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={socialSecurityNumberField.path}
              label={socialSecurityNumberField.label}
              required={socialSecurityNumberField.required}
              disabled={socialSecurityNumberField.disabled}
            >
              {(field) => (
                <Input
                  label={socialSecurityNumberField.label}
                  placeholder={
                    SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                  }
                  icon={<MdOutlineSubtitles size={16} />}
                  type="custom"
                  trim
                  inputMode="numeric"
                  maxLength={15}
                  defaultValue={socialSecurityNumberField.defaultValue}
                  {...field}
                  autoComplete="one-time-code"
                  name="social-security-nir"
                  id="social-security-nir"
                  onChange={socialSecurityNumberField.onChange}
                  onBlur={socialSecurityNumberField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={nationalityField.path}
              label={nationalityField.label}
              required={nationalityField.required}
              disabled={nationalityField.disabled}
            >
              {(field) => (
                <Select
                  icon={<MdOutlineMap size={16} />}
                  label={nationalityField.label}
                  placeholder={
                    SUBSCRIPTION.FORM.NATIONALITY?.PLACEHOLDER || "Française"
                  }
                  showSearch
                  options={(Array.isArray(NATIONALITIES)
                    ? NATIONALITIES
                    : []
                  ).map((name) => ({ value: name, label: name }))}
                  defaultValue={nationalityField.defaultValue}
                  value={get(initialValues, nationalityPath)}
                  {...field}
                  onChange={nationalityField.onChange}
                  onBlur={nationalityField.onBlur}
                />
              )}
            </FormField>
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={healthInsuranceFundField.path}
              label={healthInsuranceFundField.label}
              required={healthInsuranceFundField.required}
              disabled={healthInsuranceFundField.disabled}
            >
              {(field) => (
                <Input
                  label={healthInsuranceFundField.label}
                  placeholder={
                    SUBSCRIPTION.FORM.HEALTH_INSURANCE_FUND?.PLACEHOLDER ||
                    "ex : CPAM du Rhône"
                  }
                  icon={<MdOutlineHealthAndSafety size={16} />}
                  type="custom"
                  defaultValue={healthInsuranceFundField.defaultValue}
                  {...field}
                  onChange={healthInsuranceFundField.onChange}
                  onBlur={healthInsuranceFundField.onBlur}
                  optional
                />
              )}
            </FormField>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Adherent;
